/**
 * Post Card Component
 *
 * Displays an individual post on the Family Board
 * Features:
 * - Post header (author, timestamp)
 * - Post content (text, media)
 * - Engagement stats (likes, comments)
 * - Action buttons (like, comment, share)
 * - Comments section (expandable)
 *
 * Phase 1: Text posts, likes, basic comments
 * Phase 2: Media display, photo grid
 * Phase 3: Advanced features (edit, delete, share)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  IconButton,
  Typography,
  Button,
  Box,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions as MuiDialogActions,
  CircularProgress,
  Popover,
  Tooltip,
  Chip,
  Snackbar
} from '@mui/material';
import {
  Favorite as LikeIcon,
  FavoriteBorder as LikeOutlineIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { boardService } from '../../services/boardService';
import EmojiPicker from '../../Components/EmojiPicker';
import { useProfile } from '../../contexts/ProfileContext';
import { formatDistanceToNow } from 'date-fns';
import logger from '../../utils/logger';

// Available reactions
const REACTIONS = [
  { type: 'like', emoji: '❤️', label: 'Love' },
  { type: 'haha', emoji: '😂', label: 'Haha' },
  { type: 'wow', emoji: '😮', label: 'Wow' },
  { type: 'sad', emoji: '😢', label: 'Sad' },
  { type: 'angry', emoji: '😡', label: 'Angry' },
  { type: 'care', emoji: '🥰', label: 'Care' }
];

const PostCard = ({ post, familyCode, currentUserId }) => {
  const { profile } = useProfile();

  // State management
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [reactions, setReactions] = useState({});
  const [userReaction, setUserReaction] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isReporting, setIsReporting] = useState(false);

  // Initialize likes and reactions state
  useEffect(() => {
    setIsLiked(post.likedBy?.includes(currentUserId) || false);
    setLikesCount(post.likes || 0);
    setReactions(post.reactions || {});
    setUserReaction(post.reactions?.[currentUserId]?.type || null);
  }, [post.likedBy, post.likes, post.reactions, currentUserId]);

  /**
   * Subscribe to comments when section is expanded
   */
  useEffect(() => {
    if (!showComments || !familyCode || !post.id) return;

    const unsubscribe = boardService.subscribeToComments(
      familyCode,
      post.id,
      (updatedComments) => {
        setComments(updatedComments);
      }
    );

    return () => unsubscribe();
  }, [showComments, familyCode, post.id]);

  /**
   * Handle like/unlike
   * Note: This is kept for future implementation of simple like functionality
   */
  // eslint-disable-next-line no-unused-vars
  const handleLike = useCallback(async () => {
    try {
      // Optimistic update
      setIsLiked(prev => !prev);
      setLikesCount(prev => isLiked ? prev - 1 : prev + 1);

      // Actual update
      await boardService.toggleLike(familyCode, post.id, currentUserId);
    } catch (error) {
      logger.error('Error toggling like:', error);
      // Revert optimistic update on error
      setIsLiked(prev => !prev);
      setLikesCount(prev => isLiked ? prev + 1 : prev - 1);
    }
  }, [familyCode, post.id, currentUserId, isLiked]);

  /**
   * Handle adding a reaction
   */
  const handleReaction = useCallback(async (reactionType) => {
    try {
      // Optimistic update
      if (userReaction === reactionType) {
        // Remove reaction
        setUserReaction(null);
        const newReactions = { ...reactions };
        delete newReactions[currentUserId];
        setReactions(newReactions);
      } else {
        // Add/change reaction
        setUserReaction(reactionType);
        setReactions(prev => ({
          ...prev,
          [currentUserId]: { type: reactionType, userName: profile?.displayName }
        }));
      }

      setReactionAnchor(null);

      // Actual update
      await boardService.addReaction(
        familyCode,
        post.id,
        currentUserId,
        profile?.displayName || 'Unknown',
        reactionType
      );
    } catch (error) {
      logger.error('Error adding reaction:', error);
      // Revert on error - would need to track previous state
    }
  }, [familyCode, post.id, currentUserId, profile?.displayName, userReaction, reactions]);

  /**
   * Get reaction summary for display
   */
  const getReactionSummary = useCallback(() => {
    const counts = {};
    Object.values(reactions).forEach(r => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  }, [reactions]);

  /**
   * Handle adding a comment
   */
  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const authorData = {
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        displayName: profile?.displayName || `${profile?.firstName} ${profile?.lastName}`,
        profilePictureUrl: profile?.profilePictureUrl || ''
      };

      await boardService.addComment(
        familyCode,
        post.id,
        currentUserId,
        authorData,
        newComment
      );

      setNewComment('');
    } catch (error) {
      logger.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  }, [newComment, familyCode, post.id, currentUserId, profile]);

  /**
   * Handle keyboard shortcuts for comments
   */
  const handleCommentKeyPress = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleAddComment();
    }
  };

  /**
   * Format timestamp as relative time
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';

    try {
      // Handle Firestore Timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  };

  /**
   * Handle post deletion
   */
  const handleDeletePost = async () => {
    try {
      setIsDeleting(true);
      await boardService.deletePost(familyCode, post.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      logger.error('Error deleting post:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle editing a post
   */
  const handleStartEdit = useCallback(() => {
    setEditText(post.text || '');
    setIsEditing(true);
    setMenuAnchor(null);
  }, [post.text]);

  const handleSaveEdit = useCallback(async () => {
    if (!editText.trim()) return;

    try {
      setIsSaving(true);
      await boardService.updatePost(familyCode, post.id, {
        text: editText.trim(),
        isEdited: true
      });
      setIsEditing(false);
    } catch (error) {
      logger.error('Error updating post:', error);
    } finally {
      setIsSaving(false);
    }
  }, [familyCode, post.id, editText]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditText('');
  }, []);

  /**
   * Handle saving/bookmarking a post
   */
  const handleSavePost = useCallback(async () => {
    try {
      setMenuAnchor(null);
      const saved = await boardService.savePost(familyCode, post.id, currentUserId);
      setIsSaved(saved);
    } catch (error) {
      logger.error('Error saving post:', error);
    }
  }, [familyCode, post.id, currentUserId]);

  /**
   * Handle reporting a post
   */
  const handleReportPost = useCallback(async () => {
    try {
      setIsReporting(true);
      await boardService.reportPost(familyCode, post.id, currentUserId, reportReason);
      setReportDialogOpen(false);
      setReportReason('');
    } catch (error) {
      logger.error('Error reporting post:', error);
    } finally {
      setIsReporting(false);
    }
  }, [familyCode, post.id, currentUserId, reportReason]);

  /**
   * Handle sharing a post — uses Web Share API on supported devices,
   * falls back to copying the post link to the clipboard.
   */
  const handleShare = useCallback(async () => {
    const postUrl = `${window.location.origin}/family-board?post=${post.id}`;
    const shareText = post.text
      ? post.text.substring(0, 100) + (post.text.length > 100 ? '...' : '')
      : 'Check out this post';

    // Try native Web Share API first (mobile / supported browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post by ${post.authorName}`,
          text: shareText,
          url: postUrl
        });
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to clipboard
        if (err.name === 'AbortError') return;
      }
    }

    // Fallback: copy link to clipboard
    try {
      await navigator.clipboard.writeText(postUrl);
      setShareCopied(true);
    } catch (err) {
      logger.error('Error copying share link:', err);
    }
  }, [post.id, post.text, post.authorName]);

  /**
   * Check if current user is the author
   */
  const isAuthor = post.authorId === currentUserId;

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      {/* Post Header */}
      <CardHeader
        avatar={
          <Avatar
            src={post.authorPhoto}
            sx={{ width: 40, height: 40 }}
          >
            {post.authorName?.[0]}
          </Avatar>
        }
        action={
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
        }
        title={
          <Typography variant="subtitle1" fontWeight={600}>
            {post.authorName}
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(post.createdAt)}
            {post.isPinned && ' • Pinned'}
          </Typography>
        }
        sx={{ pb: 1 }}
      />

      {/* Post Content */}
      <CardContent sx={{ pt: 0, pb: 1 }}>
        {isEditing ? (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              disabled={isSaving}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleCancelEdit} disabled={isSaving}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleSaveEdit} disabled={isSaving || !editText.trim()}>
                {isSaving ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: post.media?.length > 0 ? 2 : 0
            }}
          >
            {post.text}
            {post.isEdited && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (edited)
              </Typography>
            )}
          </Typography>
        )}

        {/* Media (Phase 2) */}
        {post.media && post.media.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {/* Media grid will be implemented in Phase 2 */}
            <Typography variant="caption" color="text.secondary">
              📷 {post.media.length} {post.media.length === 1 ? 'photo' : 'photos'}
            </Typography>
          </Box>
        )}

        {/* Engagement Stats */}
        {(likesCount > 0 || post.commentsCount > 0) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {likesCount > 0 && `${likesCount} ${likesCount === 1 ? 'like' : 'likes'}`}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowComments(!showComments)}
            >
              {post.commentsCount > 0 && `${post.commentsCount} ${post.commentsCount === 1 ? 'comment' : 'comments'}`}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Reactions Summary */}
      {Object.keys(reactions).length > 0 && (
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {Object.entries(getReactionSummary()).map(([type, count]) => {
            const reaction = REACTIONS.find(r => r.type === type);
            return reaction ? (
              <Tooltip
                key={type}
                title={`${count} ${reaction.label}`}
              >
                <Chip
                  size="small"
                  label={`${reaction.emoji} ${count}`}
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              </Tooltip>
            ) : null;
          })}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {Object.keys(reactions).length} {Object.keys(reactions).length === 1 ? 'reaction' : 'reactions'}
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Divider />
      <CardActions
        sx={{
          justifyContent: 'space-around',
          py: 0.5
        }}
      >
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Button
            startIcon={
              userReaction
                ? <span style={{ fontSize: '1.1rem' }}>{REACTIONS.find(r => r.type === userReaction)?.emoji || '❤️'}</span>
                : (isLiked ? <LikeIcon /> : <LikeOutlineIcon />)
            }
            onClick={(e) => setReactionAnchor(e.currentTarget)}
            fullWidth
            sx={{
              color: userReaction || isLiked ? 'primary.main' : 'text.secondary',
              textTransform: 'none',
              fontWeight: userReaction || isLiked ? 600 : 400,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            {userReaction ? REACTIONS.find(r => r.type === userReaction)?.label : 'Like'}
          </Button>

          {/* Reaction Picker Popover */}
          <Popover
            open={Boolean(reactionAnchor)}
            anchorEl={reactionAnchor}
            onClose={() => setReactionAnchor(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 1,
                py: 0.5,
                boxShadow: 3
              }
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {REACTIONS.map((reaction) => (
                <Tooltip key={reaction.type} title={reaction.label}>
                  <IconButton
                    onClick={() => handleReaction(reaction.type)}
                    size="small"
                    sx={{
                      fontSize: '1.5rem',
                      transition: 'transform 0.2s',
                      bgcolor: userReaction === reaction.type ? 'action.selected' : 'transparent',
                      '&:hover': {
                        transform: 'scale(1.3)',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    {reaction.emoji}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Popover>
        </Box>

        <Button
          startIcon={<CommentIcon />}
          onClick={() => setShowComments(!showComments)}
          fullWidth
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          Comment
        </Button>
        <Button
          startIcon={shareCopied ? <CheckIcon /> : <ShareIcon />}
          onClick={handleShare}
          fullWidth
          sx={{
            color: shareCopied ? 'success.main' : 'text.secondary',
            textTransform: 'none',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          {shareCopied ? 'Link Copied!' : 'Share'}
        </Button>
      </CardActions>

      {/* Comments Section */}
      <Collapse in={showComments} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ px: 2, py: 2, bgcolor: 'background.default' }}>
          {/* Add Comment */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Avatar
              src={profile?.profilePictureUrl}
              sx={{ width: 32, height: 32 }}
            >
              {profile?.firstName?.[0]}
            </Avatar>
            <TextField
              fullWidth
              size="small"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleCommentKeyPress}
              disabled={submittingComment}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setNewComment(prev => prev + emoji)}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={handleAddComment}
                      disabled={submittingComment || !newComment.trim()}
                      color="primary"
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ),
                sx: { borderRadius: 5 }
              }}
            />
          </Box>

          {/* Comments List */}
          {comments.length > 0 ? (
            <List sx={{ p: 0 }}>
              {comments.map((comment) => (
                <ListItem
                  key={comment.id}
                  alignItems="flex-start"
                  sx={{ px: 0, py: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={comment.authorPhoto}
                      sx={{ width: 32, height: 32 }}
                    >
                      {comment.authorName?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          px: 2,
                          py: 1
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {comment.authorName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {comment.text}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ px: 2, mt: 0.5 }}>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {formatTimestamp(comment.createdAt)}
                          {comment.isEdited && ' • Edited'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No comments yet. Be the first to comment!
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {isAuthor && (
          <MenuItem onClick={handleStartEdit}>
            Edit Post
          </MenuItem>
        )}
        {isAuthor && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setDeleteDialogOpen(true);
            }}
          >
            Delete Post
          </MenuItem>
        )}
        <MenuItem onClick={handleSavePost}>
          {isSaved ? 'Unsave Post' : 'Save Post'}
        </MenuItem>
        {!isAuthor && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setReportDialogOpen(true);
            }}
          >
            Report Post
          </MenuItem>
        )}
      </Menu>

      {/* Share Copied Snackbar */}
      <Snackbar
        open={shareCopied}
        autoHideDuration={2000}
        onClose={() => setShareCopied(false)}
        message="Post link copied to clipboard"
      />

      {/* Report Post Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => !isReporting && setReportDialogOpen(false)}
      >
        <DialogTitle>Report Post</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please describe why you are reporting this post. Reports are reviewed by family admins.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Describe the issue..."
            disabled={isReporting}
          />
        </DialogContent>
        <MuiDialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={isReporting}>Cancel</Button>
          <Button
            onClick={handleReportPost}
            color="error"
            variant="contained"
            disabled={isReporting || !reportReason.trim()}
          >
            {isReporting ? <CircularProgress size={20} color="inherit" /> : 'Submit Report'}
          </Button>
        </MuiDialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Post?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <MuiDialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeletePost}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </MuiDialogActions>
      </Dialog>
    </Card>
  );
};

export default PostCard;
